import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../../../context/UserContext';
import apiFetch from '../../../context/api';
import { getImageUrl } from '../../../lib/helpers';
import { HiOutlineUser, HiOutlineLockClosed, HiOutlineCamera } from 'react-icons/hi';
import CustomerBar from '../../Elements/ButtonBar';

const ROLES = {
  superadmin: 'Super Admin',
  owner: 'Propietario',
  staff: 'Personal',
  client: 'Socio',
};

export default function Profile() {
  const [token] = useContext(UserContext);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editProfile, setEditProfile] = useState({
    first_name: '',
    last_name: '',
    address: '',
    phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!token) return;
    const fetchMe = async () => {
      try {
        const res = await apiFetch('/v1/users/me', {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error al cargar perfil');
        const data = await res.json();
        setMe(data);
        const p = data?.profile || {};
        setEditProfile({
          first_name: p.first_name ?? '',
          last_name: p.last_name ?? '',
          address: p.address ?? '',
          phone: p.phone ?? '',
        });
      } catch (e) {
        setMessage({ type: 'error', text: e.message || 'Error al cargar perfil' });
      } finally {
        setLoading(false);
      }
    };
    fetchMe();
  }, [token]);

  const profileImagePath = me?.profile?.documents_path || me?.profile?.image_path;
  const avatarUrl = profileImagePath ? getImageUrl(profileImagePath) : null;
  const displayUrl = imagePreview || avatarUrl;
  const displayName = [editProfile.first_name, editProfile.last_name].filter(Boolean).join(' ') || me?.email || 'Usuario';
  const roleLabel = ROLES[me?.access_privileges] || me?.access_privileges || 'Usuario';

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setEditProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      const data = {
        profile: {
          first_name: editProfile.first_name || undefined,
          last_name: editProfile.last_name || undefined,
          address: editProfile.address || undefined,
          phone: editProfile.phone || undefined,
        },
      };
      formData.append('data', JSON.stringify(data));
      if (imageFile) formData.append('image', imageFile);
      const res = await apiFetch('/v1/users/me', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al guardar');
      }
      const updated = await res.json();
      setMe(updated);
      setImageFile(null);
      setImagePreview(null);
      setMessage({ type: 'success', text: 'Perfil actualizado correctamente' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 8 caracteres' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify({ password: passwordForm.newPassword }));
      const res = await apiFetch('/v1/users/me', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al cambiar contraseña');
      }
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Contraseña actualizada' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al cambiar contraseña' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12 text-gray-600">
        No se pudo cargar tu perfil. Vuelve a iniciar sesión.
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full overflow-x-hidden">
      <CustomerBar
        title="Perfil"
        buttons={[]}
      />

      <div className="bg-white justify-left shadow-2xl">
        <div className="bg-white p-3 sm:p-4 rounded-lg flex items-center border border-gray-200 justify-between rounded">
          <input
            readOnly
            className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 pl-11 pr-4 rounded"
            placeholder="Tu cuenta"
            value=""
          />
        </div>
      </div>

      {message.text && (
        <div
          className={`p-2 sm:p-3 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Vista de tarjetas para móvil */}
      <div className="block md:hidden space-y-3">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all duration-200">
          <div className="flex items-center gap-3 mb-4 group">
            <div className="relative flex-shrink-0">
              {displayUrl ? (
                <img
                  src={displayUrl}
                  alt={displayName}
                  className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 group-hover:border-purple-400 transition-colors flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <label className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center cursor-pointer hover:bg-gray-700">
                <HiOutlineCamera className="w-4 h-4 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900 truncate text-base group-hover:text-purple-600 transition-colors">
                {displayName}
              </div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{me.email}</div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-0.5">Rol</span>
                <span className="font-semibold text-gray-900">{roleLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all duration-200">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            <HiOutlineUser /> Información personal
          </h2>
          <form onSubmit={saveProfile} className="wa-form space-y-3">
            <div className="wa-form-grid">
              <div className="wa-form-field">
                <label className="wa-label">Nombre</label>
                <input type="text" name="first_name" className="wa-input" value={editProfile.first_name} onChange={handleProfileChange} placeholder="Nombre" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Apellidos</label>
                <input type="text" name="last_name" className="wa-input" value={editProfile.last_name} onChange={handleProfileChange} placeholder="Apellidos" />
              </div>
            </div>
            <div className="wa-form-field">
              <label className="wa-label">Dirección</label>
              <input type="text" name="address" className="wa-input" value={editProfile.address} onChange={handleProfileChange} placeholder="Dirección" />
            </div>
            <div className="wa-form-field">
              <label className="wa-label">Teléfono</label>
              <input type="text" name="phone" className="wa-input" value={editProfile.phone} onChange={handleProfileChange} placeholder="Teléfono" />
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-3 py-2.5 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Guardar datos'}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all duration-200">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            <HiOutlineLockClosed /> Seguridad
          </h2>
          <form onSubmit={changePassword} className="wa-form space-y-3">
            <div className="wa-form-field">
              <label className="wa-label">Nueva contraseña</label>
              <input type="password" className="wa-input" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))} placeholder="Mínimo 8 caracteres" minLength={8} />
            </div>
            <div className="wa-form-field">
              <label className="wa-label">Confirmar contraseña</label>
              <input type="password" className="wa-input" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} placeholder="Repite la contraseña" minLength={8} />
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-3 py-2.5 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Cambiar contraseña'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Vista de tabla para desktop */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow border">
        <div className="p-4">
          <div className="flex items-center gap-4 mb-8">
            <div className="relative flex-shrink-0">
              {displayUrl ? (
                <img src={displayUrl} alt={displayName} className="w-28 h-28 rounded-full object-cover border-2 border-gray-200" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-2xl text-3xl font-bold">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center cursor-pointer hover:bg-gray-700">
                <HiOutlineCamera className="w-4 h-4 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">{displayName}</p>
              <p className="text-gray-500 text-sm">{me.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">{roleLabel}</span>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              <HiOutlineUser /> Información personal
            </h2>
            <form onSubmit={saveProfile} className="wa-form space-y-4">
              <div className="wa-form-grid">
                <div className="wa-form-field">
                  <label className="wa-label">Nombre</label>
                  <input type="text" name="first_name" className="wa-input" value={editProfile.first_name} onChange={handleProfileChange} placeholder="Nombre" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Apellidos</label>
                  <input type="text" name="last_name" className="wa-input" value={editProfile.last_name} onChange={handleProfileChange} placeholder="Apellidos" />
                </div>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Dirección</label>
                <input type="text" name="address" className="wa-input" value={editProfile.address} onChange={handleProfileChange} placeholder="Dirección" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Teléfono</label>
                <input type="text" name="phone" className="wa-input" value={editProfile.phone} onChange={handleProfileChange} placeholder="Teléfono" />
              </div>
              <div className="wa-form-actions">
                <button type="submit" className="wa-btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar datos'}</button>
              </div>
            </form>
          </section>

          <section>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              <HiOutlineLockClosed /> Seguridad
            </h2>
            <form onSubmit={changePassword} className="wa-form space-y-4">
              <div className="wa-form-field">
                <label className="wa-label">Nueva contraseña</label>
                <input type="password" className="wa-input" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))} placeholder="Mínimo 8 caracteres" minLength={8} />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Confirmar contraseña</label>
                <input type="password" className="wa-input" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} placeholder="Repite la contraseña" minLength={8} />
              </div>
              <div className="wa-form-actions">
                <button type="submit" className="wa-btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Cambiar contraseña'}</button>
              </div>
            </form>
          </section>
        </div>
      </div>

      <p className="mt-6 sm:mt-8 text-center text-gray-400 text-xs sm:text-sm">ClubsAgent Beta 1.0.0</p>
    </div>
  );
}
